import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import { Alert } from 'react-bootstrap';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { useBeforeUnload, useDidMount } from '../../../../hooks';
import { Error, Field } from '../../../../components/Form';
import { Modal, onHideHandler, renderShowConfirmationModal } from '../../../../components';
import { InfiniteComboBox } from '../../../../containers';
import { constants } from '../../../../redux/customers';
import { actions as countryActions, selectors as countrySelectors } from '../../../../redux/countries';
import { makePost, makeGet } from '../../../../api/ajax';
import { SUBSCRIPTION_TYPES } from '../../../../components/Cards/SubscriptionCard/SubscriptionCard';

const handleSubmit = (data, setStatus, setShowSuccess, resetForm, setHasUnsavedChanges, setTimeZones, setCountryComboKey) => {
  makePost('customers', data).subscribe(
    () => {
      setShowSuccess('Customer has been created!');
      resetForm();
      setTimeZones([]);
      setCountryComboKey((prevKey) => prevKey + 1);

      setHasUnsavedChanges(false);
    },
    ({ response: { error } }) => {
      setStatus(error);

      setHasUnsavedChanges(false);
    },
  );
};

const CustomerModal = ({ onModalClose, ...props }) => {
  const [showSuccess, setShowSuccess] = useState(null);
  const [subscriptions, setSubscriptions] = useState(null);
  const [defaultSubscription, setDefaultSubscription] = useState(null);
  const [dividedSubscriptions, setDividedSubscriptions] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [timeZones, setTimeZones] = useState([]);
  const [countryComboKey, setCountryComboKey] = useState(0);

  const dispatch = useDispatch();

  useBeforeUnload(hasUnsavedChanges);

  useDidMount(() => {
    makeGet('subscriptions').subscribe(({ response }) => {
      const sorted = _.orderBy(response, (s) => s.allowedPhotoSeries);
      setSubscriptions(sorted);
    });
  });

  useEffect(() => {
    if (!subscriptions) return;

    setDefaultSubscription(_.find(subscriptions, (s) => s.type === constants.SUBSCRIPTION_TYPES.LITE));

    const orderedSubscriptions = _.orderBy(subscriptions, 'enabledFeatures.scanVolume')
      .filter((s) => s?.type !== SUBSCRIPTION_TYPES.CORPORATE);

    setDividedSubscriptions(_.partition(
      orderedSubscriptions,
      (s) => _.indexOf(orderedSubscriptions, s) < orderedSubscriptions.length / 2,
    ));
  }, [subscriptions, setDefaultSubscription, setDividedSubscriptions]);

  if (!defaultSubscription || !dividedSubscriptions) return null;

  const initialValues = {
    companyName: '',
    companyRegistrationNo: '',
    reportEmail: '',
    moderatorFirstName: '',
    moderatorLastName: '',
    subscriptionId: defaultSubscription.id || '',
    timeZoneId: '',
    phone: '',
    askAdditionalInformation: false,
    emailConfirmationDisabled: false,
  };

  const onHideFn = () => {
    onHideHandler(hasUnsavedChanges, onModalClose, setShowConfirmModal);
  };

  return (
    <>
      {renderShowConfirmationModal(showConfirmModal, setShowConfirmModal, onModalClose)}

      <Modal size="lg" headerText="Add new customer" onHide={onHideFn} backdrop="static" {...props}>
        <Modal.Body>
          <div>
            <div className="row">
              <div className="col">
                {showSuccess && (
                  <Alert variant="success" dismissible onClick={() => setShowSuccess(null)}>
                    {showSuccess}
                  </Alert>
                )}
              </div>
            </div>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={Yup.object().shape({
                companyName: Yup.string().required('Company name is required'),
                countryCode: Yup.string().required('Country is required'),
                timeZoneId: Yup.string().required('Time zone is required'),
                companyRegistrationNo: Yup.string()
                  .required('Company registry code is required')
                  .min(2, 'Must be at least 2 characters long'),
                reportEmail: Yup.string().required('Report e-mail is required').email('Please enter a correct e-mail'),
                moderatorFirstName: Yup.string().required('First name is required'),
                moderatorLastName: Yup.string().required('Last name is required'),
                phone: Yup.number().min(5).required('Phone is required')
              })}
              onSubmit={(data, { setStatus, resetForm }) =>
                handleSubmit(data, setStatus, setShowSuccess, resetForm, setHasUnsavedChanges, setTimeZones, setCountryComboKey)}
              validateOnChange={false}
            >
              {({ errors, status, touched, values, setFieldValue }) => (
                <Form
                  onChange={({ target }) => {
                    if (!hasUnsavedChanges && target.value !== initialValues[target.name]) {
                      setHasUnsavedChanges(true);
                    }
                  }}
                >
                  <div className="row">
                    <div className="col-6 col-md-6">
                      <Field
                        type="text"
                        name="companyName"
                        label="Company name"
                        errors={errors.companyName}
                        touched={touched.companyName}
                      />
                    </div>
                    <div className="col-6 col-md-6">
                      <Field
                        name="companyRegistrationNo"
                        id="companyRegistrationNo"
                        label="Company registry code"
                        errors={errors.companyRegistrationNo}
                        touched={touched.companyRegistrationNo}
                      />
                    </div>
                    <div className="col-6 col-md-6">
                      <Field
                        type="text"
                        name="reportEmail"
                        label="Report e-mail"
                        errors={errors.reportEmail}
                        touched={touched.reportEmail}
                      />
                    </div>
                    <div className="col-6 col-md-6">
                      <InfiniteComboBox
                        key={countryComboKey}
                        label="Country"
                        inputId="countryInput"
                        loadUrl="countries"
                        searchParam="countryName"
                        pageSize={300}
                        isInternalFiltration
                        disableInfiniteScroll
                        onLoadSuccess={(countriesPaged) => dispatch(countryActions.loadCountriesSuccess(countriesPaged))}
                        resultSelector={countrySelectors.selectPagedResult}
                        isError={errors.countryCode && true}
                        error={errors.countryCode}
                        searchingDisabled={false}
                        placeholder="Search"
                        onSelect={(selectedCountry) => {
                          const countryCode = _.isEmpty(selectedCountry)
                            ? ''
                            : selectedCountry.code;

                          setFieldValue('countryCode', countryCode, true);

                          const availableTimeZones = _.isEmpty(selectedCountry)
                            ? []
                            : selectedCountry.availableTimeZoneIds || [];

                          setTimeZones(availableTimeZones);

                          const defaultTimeZone = _.find(availableTimeZones, 'isDefault');
                          setFieldValue('timeZoneId', defaultTimeZone ? defaultTimeZone.timeZoneId : '');
                        }}
                      />
                    </div>
                    <div className="col-6 col-md-6">
                      {timeZones.length > 1 && (
                        <Field
                          name="timeZoneId"
                          type="select"
                          label="Time zone"
                          errors={errors.timeZoneId}
                          touched={touched.timeZoneId}
                          options={_.map(timeZones, (tz) => ({
                            name: `${tz.displayName} (UTC${tz.utcOffset})`,
                            value: tz.timeZoneId,
                          }))}
                        />
                      )}
                      {timeZones.length === 1 && (
                        <div className="form-group">
                          <label className="mb-0 mt-1">Time zone</label>
                          <div>{`${timeZones[0].displayName} (UTC${timeZones[0].utcOffset})`}</div>
                        </div>
                      )}
                      {timeZones.length === 0 && errors.timeZoneId && <Error>{errors.timeZoneId}</Error>}
                    </div>
                  </div>
                  <div className="row mt-4 mb-3">
                    <div className="col-12 col-md-12">
                      <h6>Subscription</h6>
                      <div className="row">
                        {_.map(dividedSubscriptions, (subscriptionsGroup, groupIndex) => (
                          <div className="col-6 col-md-6" key={groupIndex}>
                            {_.map(
                              subscriptionsGroup,
                              (s, i) =>
                                s.name !== 'Smart Scan Basic' && (
                                  <div className="form-check" key={i}>
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      name="subscriptionId"
                                      value={s.id}
                                      id={s.id}
                                      checked={s.id === values.subscriptionId}
                                      onChange={() => setFieldValue('subscriptionId', s.id)}
                                    />
                                    <label className="form-check-label" htmlFor={s.id}>
                                      {`${s.name} (${s.enabledFeatures.scanVolume} scans)`}
                                    </label>
                                  </div>
                                ),
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <h6 className="mt-4">Contact person</h6>
                  <div className="row mt-3 mb-2">
                    <div className="col-6 col-md-6">
                      <Field
                        type="text"
                        name="moderatorFirstName"
                        label="First name"
                        touched={touched.moderatorFirstName}
                        errors={errors.moderatorFirstName}
                      />
                    </div>
                    <div className="col-6 col-md-6">
                      <Field
                        type="text"
                        name="moderatorLastName"
                        label="Last name"
                        touched={touched.moderatorLastName}
                        errors={errors.moderatorLastName}
                      />
                    </div>
                    <div className="col-6 col-md-6">
                      <Field type="text" name="phone" label="Phone" touched={touched.phone} errors={errors.phone} />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12 col-md-12">{status && <Error>{status}</Error>}</div>
                  </div>
                  <hr />
                  <div className="row">
                    <div className="col-12 col-md-12 text-right">
                      <button
                        className="btn btn-secondary mr-1"
                        type="reset"
                        data-testid="modalCancelBtn"
                        onClick={onHideFn}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-primary" data-testid="saveCustomerBtn">
                        Save
                      </button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

CustomerModal.propTypes = {
  onModalClose: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
};

export default CustomerModal;
